/**
 * Print dialog for a member's record (TRUS-636).
 *
 * The caseworker picks a program (or all), then what to print — member
 * profile, forms, case notes, outreach, documents — and within each, exactly
 * which sections / items. Two modes:
 *   - current information, optionally keeping empty fields as write-in lines;
 *   - a blank hand-fill copy, for a visit where there is no computer: answers
 *     go on paper and are typed in back at the office.
 *
 * Everything is fetched read-only when the dialog opens; the document itself
 * is built by printPacketBuilder and printed from a new window.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Lock, Printer } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useOptionalAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/lib/authFetch';
import { membersAPI, type Member, type ProfileField, type Enrollment as MemberEnrollment } from '@/services/api/members';
import { enrollmentsAPI, type Document as EnrollmentDocument, type Note } from '@/services/api/enrollments';
import { formsAPI, type FormInstance, type FormTemplate } from '@/services/api/forms';
import { memberOutreachApi, type MemberOutreachTouch } from '@/services/api/memberOutreach';
import {
  buildPrintPacketHtml,
  formatPacketDate,
  type PacketLabels,
  type PacketMode,
} from './printPacketBuilder';
import { buildProfilePrintSections, fallbackProfileFields } from './profilePrintSections';

export interface PrintPacketDialogProps {
  open: boolean;
  onClose: () => void;
  memberId: number;
  /** Preselect this enrollment's program. Omit to start on "All programs". */
  enrollmentId?: number | null;
}

type Category = 'profile' | 'forms' | 'notes' | 'outreach' | 'documents';
const CATEGORIES: Category[] = ['profile', 'forms', 'notes', 'outreach', 'documents'];

/** The notes endpoint returns more than the shared Note type declares. */
type PrintNote = Note & { subject?: string | null; contact_method?: string | null };

const isImage = (mime?: string | null) => !!mime && mime.toLowerCase().startsWith('image/');

async function imageAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await authFetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Open the window during the click (popup blockers allow that), and fill it
 * once the packet has been built.
 */
function writeAndPrint(w: Window, html: string) {
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 400);
}

export default function PrintPacketDialog({ open, onClose, memberId, enrollmentId }: PrintPacketDialogProps) {
  const { t } = useAppTranslation();
  const auth = useOptionalAuth();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  const [member, setMember] = useState<Member | null>(null);
  const [enrollments, setEnrollments] = useState<MemberEnrollment[]>([]);
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [forms, setForms] = useState<FormInstance[]>([]);
  const [notes, setNotes] = useState<PrintNote[]>([]);
  const [outreach, setOutreach] = useState<MemberOutreachTouch[]>([]);
  const [documents, setDocuments] = useState<EnrollmentDocument[]>([]);

  const [scope, setScope] = useState<number | 'all'>(enrollmentId ?? 'all');
  const [mode, setMode] = useState<PacketMode>('filled');
  const [emptyAsWriteIn, setEmptyAsWriteIn] = useState(true);
  const [included, setIncluded] = useState<Record<Category, boolean>>({
    profile: true,
    forms: false,
    notes: false,
    outreach: false,
    documents: false,
  });
  const [expanded, setExpanded] = useState<Category | null>('profile');
  const [pickedSections, setPickedSections] = useState<Set<string>>(new Set());
  const [pickedForms, setPickedForms] = useState<Set<number>>(new Set());
  const [pickedNotes, setPickedNotes] = useState<Set<number>>(new Set());
  const [pickedOutreach, setPickedOutreach] = useState<Set<string>>(new Set());
  const [pickedDocs, setPickedDocs] = useState<Set<number>>(new Set());
  const [blankNotePages, setBlankNotePages] = useState(2);

  // Reset to the caller's program each time the dialog opens.
  useEffect(() => {
    if (open) setScope(enrollmentId ?? 'all');
  }, [open, enrollmentId]);

  // Load everything the dialog can offer. Read-only endpoints only.
  useEffect(() => {
    if (!open || !memberId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const [m, ens, fis, touches] = await Promise.all([
          membersAPI.getMember(memberId),
          membersAPI.getMemberEnrollments(memberId).catch(() => [] as MemberEnrollment[]),
          formsAPI.listMemberFormInstances(memberId).catch(() => [] as FormInstance[]),
          memberOutreachApi.getTouches(memberId).catch(() => null),
        ]);
        const perEnrollment = await Promise.all(
          ens.map(async (e) => {
            const [ns, ds] = await Promise.all([
              enrollmentsAPI.listNotes(e.id).catch(() => [] as Note[]),
              enrollmentsAPI.listDocuments(e.id).catch(() => [] as EnrollmentDocument[]),
            ]);
            return { notes: ns as PrintNote[], docs: ds };
          }),
        );
        if (cancelled) return;
        setMember(m);
        setEnrollments(ens);
        setForms(fis);
        setOutreach(touches?.touches ?? []);
        setNotes(
          perEnrollment
            .flatMap((p) => p.notes)
            .sort((a, b) => String(b.created_ts).localeCompare(String(a.created_ts))),
        );
        setDocuments(perEnrollment.flatMap((p) => p.docs));
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.response?.data?.detail || err?.message || 'Error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, memberId]);

  const scopeEnrollment = scope === 'all' ? null : enrollments.find((e) => e.id === scope) ?? null;

  // Profile layout is per program (program-specific fields).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    membersAPI
      .getProfileFields({ program_id: scopeEnrollment?.program_id ?? undefined })
      .then((pf) => !cancelled && setProfileFields(pf))
      .catch(() => !cancelled && setProfileFields([]));
    return () => {
      cancelled = true;
    };
  }, [open, scopeEnrollment?.program_id]);

  const programName = (enrollmentIdOf?: number | null) =>
    enrollments.find((e) => e.id === enrollmentIdOf)?.program_name ?? '';

  const inScope = (eid?: number | null) => scope === 'all' || eid === scope;

  const effectiveFields = useMemo(
    () => (profileFields.length ? profileFields : fallbackProfileFields(t)),
    [profileFields, t],
  );
  // Section list for the picker: structure only, never filtered by values.
  const sectionChoices = useMemo(() => buildProfilePrintSections(effectiveFields, null), [effectiveFields]);
  // The public form that created the record has no enrollment: always offer it.
  const scopedForms = useMemo(() => forms.filter((f) => !f.enrollment_id || inScope(f.enrollment_id)), [forms, scope]);
  const scopedNotes = useMemo(() => notes.filter((n) => inScope(n.enrollment_id)), [notes, scope]);
  const scopedDocs = useMemo(() => documents.filter((d) => inScope(d.enrollment_id)), [documents, scope]);

  // Default picks: every section / item in scope, confidential notes excepted.
  useEffect(() => setPickedSections(new Set(sectionChoices.map((s) => s.key))), [sectionChoices]);
  useEffect(() => setPickedForms(new Set(scopedForms.map((f) => f.id))), [scopedForms]);
  useEffect(
    () => setPickedNotes(new Set(scopedNotes.filter((n) => !n.is_confidential).map((n) => n.id))),
    [scopedNotes],
  );
  useEffect(() => setPickedOutreach(new Set(outreach.map((o) => o.id))), [outreach]);
  useEffect(() => setPickedDocs(new Set(scopedDocs.map((d) => d.id))), [scopedDocs]);

  const blank = mode === 'blank';
  const categoryAvailable = (c: Category) => !(blank && c === 'documents');

  const counts: Record<Category, string> = {
    profile: t('printPacket.countOf', '{{picked}} of {{total}}', { picked: pickedSections.size, total: sectionChoices.length }),
    forms: t('printPacket.countOf', '{{picked}} of {{total}}', { picked: pickedForms.size, total: scopedForms.length }),
    notes: blank
      ? t('printPacket.blankSheets', '{{count}} blank sheets', { count: blankNotePages })
      : t('printPacket.countOf', '{{picked}} of {{total}}', { picked: pickedNotes.size, total: scopedNotes.length }),
    outreach: blank
      ? t('printPacket.blankLog', 'Blank contact log')
      : t('printPacket.countOf', '{{picked}} of {{total}}', { picked: pickedOutreach.size, total: outreach.length }),
    documents: t('printPacket.countOf', '{{picked}} of {{total}}', { picked: pickedDocs.size, total: scopedDocs.length }),
  };

  const categoryLabel: Record<Category, string> = {
    profile: t('printPacket.category.profile', 'Member profile'),
    forms: t('printPacket.category.forms', 'Forms & assessments'),
    notes: t('printPacket.category.notes', 'Case notes'),
    outreach: t('printPacket.category.outreach', 'Outreach'),
    documents: t('printPacket.category.documents', 'Documents'),
  };

  const anythingToPrint = CATEGORIES.some((c) => {
    if (!included[c] || !categoryAvailable(c)) return false;
    if (blank && (c === 'notes' || c === 'outreach')) return true;
    return {
      profile: pickedSections.size,
      forms: pickedForms.size,
      notes: pickedNotes.size,
      outreach: pickedOutreach.size,
      documents: pickedDocs.size,
    }[c] > 0;
  });

  const labels = (): Partial<PacketLabels> => ({
    packetTitle: t('printPacket.out.packetTitle', 'Member Record'),
    blankPacketTitle: t('printPacket.out.blankPacketTitle', 'Member Record — Hand-fill Copy'),
    member: t('printPacket.out.member', 'Member'),
    memberNumber: t('printPacket.out.memberNumber', 'Member #'),
    dateOfBirth: t('printPacket.out.dateOfBirth', 'Date of birth'),
    program: t('printPacket.out.program', 'Program'),
    printed: t('printPacket.out.printed', 'Printed'),
    printedBy: t('printPacket.out.printedBy', 'Printed by'),
    confidential: t(
      'printPacket.out.confidential',
      'Confidential — contains protected member information. Store securely and shred when no longer needed.',
    ),
    profile: t('printPacket.out.profile', 'Member Profile'),
    caseNotes: t('printPacket.out.caseNotes', 'Case Notes'),
    caseNote: t('printPacket.out.caseNote', 'Case Note'),
    outreach: t('printPacket.out.outreach', 'Outreach'),
    documents: t('printPacket.out.documents', 'Documents'),
    date: t('printPacket.out.date', 'Date'),
    author: t('printPacket.out.author', 'Staff'),
    type: t('printPacket.out.type', 'Type'),
    subject: t('printPacket.out.subject', 'Subject'),
    notes: t('printPacket.out.notes', 'Notes'),
    contactMethod: t('printPacket.out.contactMethod', 'Contact method'),
    contactMethods: [
      t('printPacket.out.methodInPerson', 'In person'),
      t('printPacket.out.methodPhone', 'Phone'),
      t('printPacket.out.methodText', 'Text'),
      t('printPacket.out.methodEmail', 'Email'),
      t('printPacket.out.methodOther', 'Other'),
    ],
    followUp: t('printPacket.out.followUp', 'Follow-up needed'),
    followUpDate: t('printPacket.out.followUpDate', 'Follow-up date'),
    yes: t('printPacket.out.yes', 'Yes'),
    no: t('printPacket.out.no', 'No'),
    staffSignature: t('printPacket.out.staffSignature', 'Staff signature'),
    channel: t('printPacket.out.channel', 'Channel'),
    direction: t('printPacket.out.direction', 'Direction'),
    minutes: t('printPacket.out.minutes', 'Minutes'),
    location: t('printPacket.out.location', 'Location'),
    form: t('printPacket.out.form', 'Form'),
    name: t('printPacket.out.name', 'Name'),
    uploaded: t('printPacket.out.uploaded', 'Uploaded'),
    uploadedBy: t('printPacket.out.uploadedBy', 'Uploaded by'),
    documentsNote: t(
      'printPacket.out.documentsNote',
      'Only image files print here. Open PDFs and other files from the Documents tab to print them.',
    ),
    noItems: t('printPacket.out.noItems', 'None on file.'),
    checkOne: t('printPacket.out.checkOne', 'Check one'),
    checkAll: t('printPacket.out.checkAll', 'Check all that apply'),
    filledBy: t('printPacket.out.filledBy', 'Filled in by'),
  });

  const handlePrint = async () => {
    setPrintError(null);
    const w = window.open('', '_blank');
    if (!w) {
      setPrintError(t('printPacket.popupBlocked', 'Your browser blocked the print window. Allow pop-ups for this site and try again.'));
      return;
    }
    w.document.write(`<p style="font-family:sans-serif;padding:24px">${t('printPacket.preparing', 'Preparing your printout…')}</p>`);
    setPrinting(true);
    try {
      const want = (c: Category) => included[c] && categoryAvailable(c);

      // Templates for the chosen forms (one fetch per distinct template).
      const chosenForms = want('forms') ? scopedForms.filter((f) => pickedForms.has(f.id)) : [];
      const templateIds = Array.from(new Set(chosenForms.map((f) => f.form_template_id)));
      const templates = new Map<number, FormTemplate>();
      await Promise.all(
        templateIds.map(async (id) => {
          try {
            templates.set(id, await formsAPI.getFormTemplate(id));
          } catch {
            /* skipped below */
          }
        }),
      );

      const chosenDocs = want('documents') ? scopedDocs.filter((d) => pickedDocs.has(d.id)) : [];
      const docImages = await Promise.all(
        chosenDocs.map((d) => (isImage(d.mime_type) && d.file_url ? imageAsDataUrl(d.file_url) : Promise.resolve(null))),
      );

      const html = buildPrintPacketHtml({
        mode,
        emptyAsWriteIn,
        header: {
          memberName: member ? [member.first_name, member.last_name].filter(Boolean).join(' ') : null,
          memberNumber: (member as any)?.member_number ?? null,
          dateOfBirth: (member as any)?.date_of_birth ?? (member as any)?.dob ?? null,
          programName: scopeEnrollment?.program_name ?? null,
          printedBy: auth?.user?.name ?? null,
          printedAt: new Date(),
        },
        profile: want('profile')
          ? buildProfilePrintSections(effectiveFields, blank ? null : member).filter((s) => pickedSections.has(s.key))
          : undefined,
        forms: chosenForms
          .filter((f) => templates.has(f.form_template_id))
          .map((f) => ({
            title: f.form_template_name,
            subtitle: blank
              ? null
              : [
                  f.enrollment_id ? programName(f.enrollment_id) : null,
                  f.status,
                  formatPacketDate(f.submitted_at || f.last_updated_ts || f.created_ts),
                ]
                  .filter(Boolean)
                  .join(' · '),
            template: templates.get(f.form_template_id)!,
            fieldData: f.field_data,
          })),
        notes: want('notes')
          ? scopedNotes
              .filter((n) => pickedNotes.has(n.id))
              .map((n) => ({
                date: n.created_ts,
                author: n.created_by_name,
                type: n.note_type,
                subject: n.subject,
                body: n.note_text,
                programName: scope === 'all' ? programName(n.enrollment_id) : null,
              }))
          : undefined,
        blankNotePages,
        outreach: want('outreach')
          ? outreach
              .filter((o) => pickedOutreach.has(o.id))
              .map((o) => ({
                date: o.date,
                channel: o.channel,
                direction: o.direction,
                minutes: o.minutes,
                engagementType: o.engagement_type,
                location: o.location,
                notes: o.notes,
                formName: o.form_name,
              }))
          : undefined,
        documents: chosenDocs.length
          ? chosenDocs.map((d, i) => ({
              name: d.document_name || d.file_name,
              type: d.document_type,
              programName: programName(d.enrollment_id),
              uploadedAt: d.upload_date,
              uploadedBy: d.uploaded_by_name,
              notes: d.notes,
              imageDataUrl: docImages[i],
            }))
          : undefined,
        labels: labels(),
      });
      writeAndPrint(w, html);
      const skipped = chosenForms.length - chosenForms.filter((f) => templates.has(f.form_template_id)).length;
      if (skipped > 0) {
        setPrintError(t('printPacket.formsSkipped', '{{count}} form(s) could not be loaded and were left out.', { count: skipped }));
      }
    } catch (err: any) {
      w.close();
      setPrintError(err?.message || t('printPacket.printFailed', 'Could not build the printout.'));
    } finally {
      setPrinting(false);
    }
  };

  const toggle = <T,>(set: Set<T>, value: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const SelectAll = ({ onAll, onNone }: { onAll: () => void; onNone: () => void }) => (
    <div className="flex gap-3 text-xs mb-2">
      <button type="button" className="text-teal-700 dark:text-teal-400 hover:underline" onClick={onAll}>
        {t('printPacket.selectAll', 'Select all')}
      </button>
      <button type="button" className="text-teal-700 dark:text-teal-400 hover:underline" onClick={onNone}>
        {t('printPacket.selectNone', 'Clear')}
      </button>
    </div>
  );

  const Row = ({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) => (
    <label className="flex items-start gap-2 py-1 text-sm cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <span className="min-w-0">{children}</span>
    </label>
  );

  const empty = (
    <p className="text-sm text-gray-500 dark:text-gray-400 py-1">{t('printPacket.nothingInScope', 'Nothing on file for this program.')}</p>
  );

  const renderItems = (c: Category) => {
    switch (c) {
      case 'profile':
        return sectionChoices.length ? (
          <>
            <SelectAll
              onAll={() => setPickedSections(new Set(sectionChoices.map((s) => s.key)))}
              onNone={() => setPickedSections(new Set())}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              {sectionChoices.map((s) => (
                <Row key={s.key} checked={pickedSections.has(s.key)} onToggle={() => toggle(pickedSections, s.key, setPickedSections)}>
                  {s.title} <span className="text-xs text-gray-400">{`(${s.fields.length})`}</span>
                </Row>
              ))}
            </div>
          </>
        ) : (
          empty
        );
      case 'forms':
        return scopedForms.length ? (
          <>
            <SelectAll onAll={() => setPickedForms(new Set(scopedForms.map((f) => f.id)))} onNone={() => setPickedForms(new Set())} />
            {scopedForms.map((f) => (
              <Row key={f.id} checked={pickedForms.has(f.id)} onToggle={() => toggle(pickedForms, f.id, setPickedForms)}>
                <span className="font-medium">{f.form_template_name}</span>{' '}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {[
                    f.enrollment_id ? programName(f.enrollment_id) : t('printPacket.submittedOnline', 'Submitted online'),
                    f.status,
                    formatPacketDate(f.submitted_at || f.last_updated_ts),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </Row>
            ))}
          </>
        ) : (
          empty
        );
      case 'notes':
        if (blank) {
          return (
            <label className="flex items-center gap-2 text-sm">
              {t('printPacket.blankNoteSheets', 'Blank note sheets')}
              <input
                type="number"
                min={1}
                max={10}
                value={blankNotePages}
                onChange={(e) => setBlankNotePages(Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
                className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              />
            </label>
          );
        }
        return scopedNotes.length ? (
          <>
            <SelectAll onAll={() => setPickedNotes(new Set(scopedNotes.map((n) => n.id)))} onNone={() => setPickedNotes(new Set())} />
            <div className="max-h-56 overflow-y-auto pr-1">
              {scopedNotes.map((n) => (
                <Row key={n.id} checked={pickedNotes.has(n.id)} onToggle={() => toggle(pickedNotes, n.id, setPickedNotes)}>
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{formatPacketDate(n.created_ts)}</span>
                  {n.is_confidential && <Lock className="inline w-3 h-3 mr-1 text-amber-600" aria-label={t('printPacket.confidential', 'Confidential')} />}
                  <span className="break-words">{(n.subject || n.note_text || '').slice(0, 90)}</span>
                </Row>
              ))}
            </div>
            {scopedNotes.some((n) => n.is_confidential) && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                {t('printPacket.confidentialHint', 'Confidential notes are not selected by default.')}
              </p>
            )}
          </>
        ) : (
          empty
        );
      case 'outreach':
        if (blank) {
          return <p className="text-sm text-gray-500 dark:text-gray-400">{t('printPacket.blankLogHint', 'Prints an empty contact log to fill in by hand.')}</p>;
        }
        return outreach.length ? (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('printPacket.outreachAllPrograms', 'Outreach is kept per member, across all programs.')}</p>
            <SelectAll onAll={() => setPickedOutreach(new Set(outreach.map((o) => o.id)))} onNone={() => setPickedOutreach(new Set())} />
            <div className="max-h-56 overflow-y-auto pr-1">
              {outreach.map((o) => (
                <Row key={o.id} checked={pickedOutreach.has(o.id)} onToggle={() => toggle(pickedOutreach, o.id, setPickedOutreach)}>
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{formatPacketDate(o.date)}</span>
                  {[o.channel, o.engagement_type, (o.notes || '').slice(0, 60)].filter(Boolean).join(' · ')}
                </Row>
              ))}
            </div>
          </>
        ) : (
          empty
        );
      case 'documents':
        if (blank) {
          return <p className="text-sm text-gray-500 dark:text-gray-400">{t('printPacket.documentsFilledOnly', 'Documents print only with current information.')}</p>;
        }
        return scopedDocs.length ? (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('printPacket.documentsHint', 'Prints a list of the documents, plus any images. Open PDFs from the Documents tab to print them.')}
            </p>
            <SelectAll onAll={() => setPickedDocs(new Set(scopedDocs.map((d) => d.id)))} onNone={() => setPickedDocs(new Set())} />
            <div className="max-h-56 overflow-y-auto pr-1">
              {scopedDocs.map((d) => (
                <Row key={d.id} checked={pickedDocs.has(d.id)} onToggle={() => toggle(pickedDocs, d.id, setPickedDocs)}>
                  {d.document_name || d.file_name}{' '}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {[d.document_type, formatPacketDate(d.upload_date)].filter(Boolean).join(' · ')}
                  </span>
                </Row>
              ))}
            </div>
          </>
        ) : (
          empty
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            {t('printPacket.title', 'Print')}
            {member && <span className="font-normal text-gray-500 dark:text-gray-400">{`— ${[member.first_name, member.last_name].filter(Boolean).join(' ')}`}</span>}
          </DialogTitle>
          <DialogDescription>
            {t('printPacket.description', 'Choose what to print. Print current information, or a blank copy to fill in by hand during a visit.')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-10 justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('printPacket.loading', 'Loading the record…')}
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-600 py-6">{t('printPacket.loadFailed', 'Could not load this record.')} {loadError}</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('printPacket.program', 'Program')}</span>
                <select
                  value={scope === 'all' ? 'all' : String(scope)}
                  onChange={(e) => setScope(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5"
                >
                  <option value="all">{t('printPacket.allPrograms', 'All programs')}</option>
                  {enrollments.map((e) => (
                    <option key={e.id} value={e.id}>
                      {[e.program_name || `#${e.id}`, e.status].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="text-sm">
                <legend className="font-medium text-gray-700 dark:text-gray-300">{t('printPacket.printAs', 'Print as')}</legend>
                <label className="flex items-center gap-2 mt-1 cursor-pointer">
                  <input type="radio" name="print-mode" checked={mode === 'filled'} onChange={() => setMode('filled')} />
                  {t('printPacket.modeFilled', 'Current information')}
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="print-mode" checked={mode === 'blank'} onChange={() => setMode('blank')} />
                  {t('printPacket.modeBlank', 'Blank copy to fill in by hand')}
                </label>
                {mode === 'filled' && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <Checkbox checked={emptyAsWriteIn} onCheckedChange={setEmptyAsWriteIn} />
                    <span>{t('printPacket.keepEmpty', 'Keep empty fields as blank lines to fill in')}</span>
                  </label>
                )}
              </fieldset>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
              {CATEGORIES.map((c) => {
                const available = categoryAvailable(c);
                const isOpen = expanded === c;
                return (
                  <div key={c} className={available ? '' : 'opacity-60'}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Checkbox
                        checked={included[c] && available}
                        disabled={!available}
                        onCheckedChange={(v) => {
                          setIncluded((prev) => ({ ...prev, [c]: v }));
                          if (v) setExpanded(c);
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : c)}
                        className="flex-1 flex items-center justify-between text-left text-sm font-medium text-gray-900 dark:text-gray-100"
                        aria-expanded={isOpen}
                      >
                        <span>{categoryLabel[c]}</span>
                        <span className="flex items-center gap-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                          {available && counts[c]}
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </span>
                      </button>
                    </div>
                    {isOpen && <div className="px-9 pb-3">{renderItems(c)}</div>}
                  </div>
                );
              })}
            </div>

            {printError && <p className="text-sm text-red-600">{printError}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('printPacket.cancel', 'Cancel')}
          </Button>
          <Button onClick={handlePrint} disabled={loading || !!loadError || printing || !anythingToPrint}>
            {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            {t('printPacket.print', 'Print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
